import { useId } from 'react';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import { getVenueStageSelection } from '@/utils/selectionDefaults';

interface VenueOption {
  id: string;
  name: string;
}

interface StageOption extends VenueOption {
  venue: string;
}

interface Selection {
  venue: string;
  stage: string;
}

interface VenueStageFieldsProps {
  venues: readonly VenueOption[];
  stages: readonly StageOption[];
  value: Selection;
  onChange: (selection: Selection) => void;
  disabled?: boolean;
}

/** Shared booking controls; callers resolve initial defaults after loading choices. */
export function VenueStageFields({ venues, stages, value, onChange, disabled = false }: VenueStageFieldsProps) {
  const id = useId();
  const availableStages = stages.filter((stage) => stage.venue === value.venue);
  const stageHelp = !value.venue ? 'Select a venue first.'
    : availableStages.length === 0 ? 'No stages available for this venue.' : undefined;
  return (
    <>
      <FormControl fullWidth required disabled={disabled || venues.length === 0}>
        <InputLabel id={`${id}-venue-label`}>Venue</InputLabel>
        <Select
          labelId={`${id}-venue-label`}
          value={value.venue}
          label="Venue"
          inputProps={{ 'aria-describedby': venues.length === 0 ? `${id}-venue-help` : undefined }}
          onChange={(event) => onChange(getVenueStageSelection(venues, stages, { venue: event.target.value }))}
        >
          {venues.map((venue) => <MenuItem key={venue.id} value={venue.id}>{venue.name}</MenuItem>)}
        </Select>
        {venues.length === 0 && <FormHelperText id={`${id}-venue-help`}>No venues available.</FormHelperText>}
      </FormControl>
      <FormControl fullWidth required disabled={disabled || !value.venue || availableStages.length === 0}>
        <InputLabel id={`${id}-stage-label`}>Stage</InputLabel>
        <Select
          labelId={`${id}-stage-label`}
          value={value.stage}
          label="Stage"
          inputProps={{ 'aria-describedby': stageHelp ? `${id}-stage-help` : undefined }}
          onChange={(event) => onChange({ ...value, stage: event.target.value })}
        >
          {availableStages.map((stage) => <MenuItem key={stage.id} value={stage.id}>{stage.name}</MenuItem>)}
        </Select>
        {stageHelp && <FormHelperText id={`${id}-stage-help`}>{stageHelp}</FormHelperText>}
      </FormControl>
    </>
  );
}
